import { type ReactNode } from 'react';
import { Inbox } from 'lucide-react';
import { cn } from './cn';

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_CLASSES = {
  sm: { wrapper: 'py-8 px-4', iconBox: 'h-8 w-8', icon: 'h-4 w-4', title: 'text-sm', description: 'text-xs' },
  md: { wrapper: 'py-12 px-6', iconBox: 'h-10 w-10', icon: 'h-5 w-5', title: 'text-sm', description: 'text-xs' },
  lg: { wrapper: 'py-16 px-8', iconBox: 'h-12 w-12', icon: 'h-6 w-6', title: 'text-base', description: 'text-sm' },
} as const;

export function EmptyState({ icon, title, description, action, size = 'md' }: EmptyStateProps) {
  const s = SIZE_CLASSES[size];

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-lg border border-dashed border-border-subtle bg-surface-1/50 text-center',
        s.wrapper,
      )}
    >
      <div className={cn('flex items-center justify-center rounded-full bg-surface-2 text-text-tertiary', s.iconBox)}>
        {icon ? <span className={cn('inline-flex', s.icon)}>{icon}</span> : <Inbox className={s.icon} />}
      </div>

      <div className="space-y-1 max-w-xs">
        <p className={cn('font-semibold text-text-secondary', s.title)}>{title}</p>
        {description && <p className={cn('text-text-tertiary', s.description)}>{description}</p>}
      </div>

      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}
